import React from 'react'
import styled from 'styled-components'
import Box from '@zuto/core/src/box'
import {
  ZutoLogo,
  ZutoLogoExperimental,
  ZutoLogoMedium,
  ZutoLogoSmallDesktop,
  ZutoLogoSmallMobile,
  QuoteZoneLogoMobile,
  PoweredByZuto,
  MoneySuperMarket
} from '@zuto/icons'
import { styled as MuiStyled } from '@mui/material'
import { SecureDataIcon } from '@zuto/atoms/src/SecureData/SecureDataIcon'
import { KnownSource } from '../../../../src/types/Source'
import TrustPilotHeader from '../../../../src/components/TrustPilotHeader'
import { useDetectAdBlock } from 'adblock-detect-react'
import TrustpilotStaticReviews from '../../../../src/components/TrustpilotStaticReviews'

const StyledHeader = styled(Box)`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  justify-content: center;
  &.clearscore-review-page-header,
  &.totallymoney-review-page-header {
    min-height: 40px;
  }
  &.moneysupermarket-review-page-header {
    min-height: 72px;
  }
  &.formless-cta-journey {
    border-top: 8px solid #ffe70c;
    margin-top: 0;
    padding-top: 10px;
  }
`

const StyledLogoWrapper = styled(Box)`
  max-width: 752px;
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const HeaderRightWrapper = MuiStyled('div')({})
const HeaderRightWrapperDefault = MuiStyled('div')({})
const AdditionalLogoWrapper = MuiStyled('div')({})

const styles = {
  display: 'flex',
  alignItems: 'flex-start',
  '&.default': {
    alignItems: 'flex-end'
  },
  '&.additional-logo': {
    alignItems: 'unset'
  },
  HeaderRightWrapperDefault: {
    '&.additional-logo': {
      display: 'flex',
      alignItems: 'center',
      '&.mobile': {
        flexDirection: 'column',
        alignItems: 'flex-end',
        '& > div': {
          marginTop: '5px'
        }
      }
    }
  },
  AdditionalLogoWrapper: {
    alignItems: 'flex-end',
    display: 'flex',
    marginLeft: '20px'
  },
  SecureDataIconWithAdditionalLogoWrapper: {
    alignItems: 'center',
    display: 'flex',
    gap: '10px',
    '&.mobile-additional-logo': {
      flexDirection: 'column',
      alignItems: 'center',
      gap: '10px'
    }
  }
}

interface HeaderProps {
  isTwoPagesJourneyExperiment: boolean
  trustpilotOnClick: () => void
  logo?: React.ReactElement
  logoLabel?: string
  showRightIcon?: boolean
  secureDataIconAtTop?: boolean
  reducedHeaderSize?: boolean
  isMobile?: boolean
  largerZutoLogo?: boolean
  additionalLogo?: React.ReactElement
  isReviewPage?: boolean
  source?: KnownSource
  isTrustpilotV1?: boolean
  isInMSMExperiment?: boolean
  isInFormlessCtaJourney?: boolean
}

const Header = ({
  trustpilotOnClick,
  logo,
  logoLabel,
  showRightIcon = true,
  isTwoPagesJourneyExperiment,
  secureDataIconAtTop = false,
  reducedHeaderSize,
  largerZutoLogo,
  isMobile,
  additionalLogo,
  isReviewPage,
  source,
  isTrustpilotV1 = false,
  isInMSMExperiment = false,
  isInFormlessCtaJourney = false
}: HeaderProps): JSX.Element => {
  const adBlockDetected = useDetectAdBlock()
  const reducedHeaderZutoLogo =
    reducedHeaderSize && isMobile ? (
      <ZutoLogoSmallMobile />
    ) : (
      <ZutoLogoSmallDesktop />
    )
  const largerZutoLogoImg = largerZutoLogo ? (
    <ZutoLogoMedium />
  ) : (
    reducedHeaderZutoLogo
  )

  const renderLogo = (): JSX.Element | null => {
    if (source === "quotezone" && isMobile) {
      return <QuoteZoneLogoMobile aria-label="QuoteZone Logo" />
    }

    if (!logo) {
      if (!isTrustpilotV1) {
        return (
          <TrustpilotStaticReviews
            onClick={trustpilotOnClick}
            width={80}
            isAboveTheFold
          />
        )
      }
      return <></>
    }

    const filterLogoProps = ({
      id,
      width,
      height,
      viewBox,
      children,
      fill
    }: Record<string, string>) => ({
      id,
      width,
      height,
      viewBox,
      children,
      fill
    })

    const props = {
      ...filterLogoProps(logo.props),
      "data-testid": "header-logo",
      "aria-label": logoLabel
    }

    return React.createElement(logo.type, props)
  }

  const renderRightIcon = () => (showRightIcon ? renderLogo() : <></>)

  const renderAdditionalRightLogo = (): JSX.Element | null => {
    if (!additionalLogo) {
      return <></>
    }
    const props = {
      ...additionalLogo.props,
      "data-testid": "additional-header-logo"
    }
    return React.createElement(additionalLogo.type, props)
  }

  const headerMargins = {
    default: "0px",
    reducedHeaderSize: "12px",
    largerZutoLogo: "10px"
  }

  const headerHeight = {
    mobile: "20px",
    default: "32px",
    largerZutoLogo: "30px"
  }

  const renderedHeaderMargins = reducedHeaderSize
    ? headerMargins.reducedHeaderSize
    : headerMargins.default
  const renderedHeaderHeight = isMobile
    ? headerHeight.mobile
    : headerHeight.default

  return (
    <>
      <StyledHeader
        as="header"
        mt={
          largerZutoLogo ? headerMargins.largerZutoLogo : renderedHeaderMargins
        }
        mb={
          largerZutoLogo ? headerMargins.largerZutoLogo : renderedHeaderMargins
        }
        pl={[4, 0]}
        pr={[4, 0]}
        mx="auto"
        className={[
          isReviewPage ? `${source}-review-page-header` : null,
          isInFormlessCtaJourney ? "formless-cta-journey" : null
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <StyledLogoWrapper>
          {isInMSMExperiment ? (
            <Box
              display="flex"
              justifyContent="space-between"
              width="100%"
              alignItems="center"
              mt="6px"
            >
              <MoneySuperMarket data-testid="moneysupermarket-header-logo" />
              <Box
                as="a"
                href="/"
                data-testid="powered-by-zuto-logo"
                mt={0}
                mb={0}
                aria-label="Zuto home"
                height="20px"
              >
                <PoweredByZuto />
              </Box>
            </Box>
          ) : (
            <>
              {reducedHeaderSize || largerZutoLogo ? (
                <Box
                  as="a"
                  href="/"
                  data-testid="zuto-logo"
                  mt={0}
                  mb={0}
                  height={
                    largerZutoLogo
                      ? headerHeight.largerZutoLogo
                      : renderedHeaderHeight
                  }
                  aria-label="Zuto home"
                >
                  {isTwoPagesJourneyExperiment ? (
                    <ZutoLogoExperimental />
                  ) : (
                    largerZutoLogoImg
                  )}
                </Box>
              ) : (
                <Box
                  as="a"
                  href="/"
                  data-testid="zuto-logo"
                  mt={5}
                  mb={5}
                  height="32px"
                  aria-label="Zuto home"
                >
                  {isTwoPagesJourneyExperiment ? (
                    <ZutoLogoExperimental />
                  ) : (
                    <ZutoLogo />
                  )}
                </Box>
              )}
              {secureDataIconAtTop && !isInFormlessCtaJourney ? (
                <HeaderRightWrapper
                  sx={styles}
                  className={!logo ? "default" : ""}
                >
                  <SecureDataIcon
                    variant={!logo && isTrustpilotV1 ? "bold" : "small"}
                    flexDirection="column"
                    alignItems="center"
                    marginRight="16px"
                  />
                  {!additionalLogo && renderRightIcon()}
                  {additionalLogo && (
                    <AdditionalLogoWrapper
                      sx={styles.SecureDataIconWithAdditionalLogoWrapper}
                      className={isMobile ? "mobile-additional-logo" : ""}
                    >
                      {renderRightIcon()}
                      {renderAdditionalRightLogo()}
                    </AdditionalLogoWrapper>
                  )}
                </HeaderRightWrapper>
              ) : (
                <HeaderRightWrapperDefault
                  sx={styles.HeaderRightWrapperDefault}
                  className={[
                    additionalLogo ? "additional-logo" : "",
                    isMobile ? "mobile" : ""
                  ].join(" ")}
                >
                  {renderRightIcon()}
                  {additionalLogo && (
                    <AdditionalLogoWrapper sx={styles.AdditionalLogoWrapper}>
                      {renderAdditionalRightLogo()}
                    </AdditionalLogoWrapper>
                  )}
                </HeaderRightWrapperDefault>
              )}
            </>
          )}
        </StyledLogoWrapper>
      </StyledHeader>
      {isTrustpilotV1 && <TrustPilotHeader adBlockDetected={adBlockDetected} />}
    </>
  )
}

export default Header
